/**
 * 구조화 이벤트 로그 — 연대기의 원천 (지시서 3.6). 텍스트가 아니라 데이터로 저장.
 * 렌더링(피드/연대기)은 EventType + payload → i18n 키로 client에서 조합.
 * id는 tick+시퀀스로 결정적으로 생성(난수 아님).
 */
import type { EventActors, EventType, GameEvent, Vec2 } from '@villager/shared';

export class EventLog {
  private events: GameEvent[] = [];
  /**
   * 영속화 대기열. events는 표시용 링버퍼라 오래된 게 잘리지만,
   * 이 큐는 "DB에 저장 확정될 때까지" 보관한다(전송 실패 시 재시도 가능).
   */
  private pending: GameEvent[] = [];
  private seq = 0;
  private readonly max: number;
  private readonly maxPending: number;
  /** 대기열 초과로 버려진 이벤트 수 (관측용) */
  droppedPending = 0;

  constructor(max = 5000, maxPending = 20000) {
    this.max = max;
    this.maxPending = maxPending;
  }

  emit(
    tick: number,
    type: EventType,
    actors: EventActors = {},
    pos?: Vec2,
    payload?: Record<string, unknown>,
  ): GameEvent {
    const e: GameEvent = { id: `e${tick}_${this.seq++}`, tick, type, actors };
    if (pos) e.pos = pos;
    if (payload) e.payload = payload;
    this.events.push(e);
    if (this.events.length > this.max) this.events.splice(0, this.events.length - this.max);

    this.pending.push(e);
    if (this.pending.length > this.maxPending) {
      const over = this.pending.length - this.maxPending;
      this.pending.splice(0, over);
      this.droppedPending += over;
    }
    return e;
  }

  /** 영속화할 이벤트를 미리 본다(아직 큐에서 빼지 않음). */
  peekPending(max = 500): readonly GameEvent[] {
    return this.pending.slice(0, max);
  }

  /** 저장 확정된 만큼 큐에서 제거. */
  commitPending(n: number): void {
    if (n > 0) this.pending.splice(0, n);
  }

  get pendingCount(): number {
    return this.pending.length;
  }

  /** 전체(보존 한도 내). */
  get all(): readonly GameEvent[] {
    return this.events;
  }

  /** 특정 이벤트 이후 새로 생긴 것들 (client 피드 증분 렌더용). */
  after(index: number): readonly GameEvent[] {
    return this.events.slice(Math.max(0, index));
  }

  get count(): number {
    return this.seq;
  }
}
